<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="landing-content">

      <!-- Overview back link -->
      <div class="overview-link" @click="onClick('chessvis_landing_page',t('Welcome to Chessvis'),'program')">
        <ion-icon :icon="chevronBackOutline" class="overview-link-icon" aria-hidden="true" />
        <span>{{ t("ChessVis Overview") }}</span>
      </div>

      <!-- Hero -->
      <section class="hero">
        <div class="hero-icon-wrap">
          <ion-icon :icon="libraryOutline" class="hero-icon" aria-hidden="true" />
        </div>
        <h1 class="hero-title">{{ t("Studio") }}</h1>
        <p class="hero-tagline">{{ t("Where games are prepared and won.") }}</p>
        <p class="hero-sub">{{ t("Opening mastery, repertoire building, AI coaching, and game analysis — the tools serious players use to improve between games.") }}</p>
        <div class="hero-badges">
          <span class="badge badge-free">{{ t("Some features free") }}</span>
          <span class="badge badge-sub">{{ t("Full access with subscription") }}</span>
        </div>
      </section>

      <!-- Opening Mastery -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap purple">
            <ion-icon :icon="trophyOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Opening Mastery") }}</h2>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("A different approach") }}</div>
          <h3>{{ t("Study the Moves You'll Actually See") }}</h3>
          <p>{{ t("Most opening tools show you everything — including long move sequences you'll almost never face. Opening Mastery focuses your study time on the moves that matter at your rating level, against players like you.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("200+ Million Real Games") }}</h3>
          <p>{{ t("Built on Lichess Rapid games — longer time controls where players actually think. Only head-to-head games within the same 50-point rating bracket are included. Why? Because even a 50-point rating difference skews how people play. You're studying your peers, not the masters.") }}</p>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">Unique to ChessVis</div>
          <h3>{{ t("See Into the Middle Game") }}</h3>
          <p>{{ t("This is the only program that shows you how your opening moves affect the middle game. Move beyond win/loss percentages — why judge a first move by how a game ends 40 moves later? See what actually happens in the positions your openings create.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Spot and Avoid Traps") }}</h3>
          <p>{{ t("When you focus on the middle game, your opponent's trappy lines reveal themselves quickly — and show you exactly how to sidestep them.") }}</p>
        </div>

        <div class="feature-card highlight">
        <div class="feature-card-label">{{ t("With Subscription") }}</div>
          <h3>{{ t("Let AI Make Sense of the Data") }}</h3>
          <p>{{ t("The numbers tell a story — AI reads it for you. Get recommendations on the moves to consider and the moves to avoid, explained in plain language, based on how players at your rating actually perform.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Analyze Your Own Games") }}</h3>
          <p>{{ t("Download your games from chess.com and lichess.org and run them through the Opening Explorer. See where you're deviating from your plan and what the data says about the positions you're reaching.") }}</p>
        </div>
      </section>

      <!-- Repertoires -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap purple">
            <ion-icon :icon="bookOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Repertoires") }}</h2>
          <span class="sub-badge">{{ t("Subscriber") }}</span>
        </div>

        <p class="section-intro">{{ t("Build a repertoire grounded in what you'll actually see — not theoretical lines that disappear after move 10.") }}</p>

        <div class="feature-card">
          <h3>{{ t("Build") }}</h3>
          <p>{{ t("An interactive repertoire builder that lets you construct your opening lines move by move, informed by the Opening Mastery data you already have.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Tree View") }}</h3>
          <p>{{ t("See your entire repertoire as a branching tree. Understand where your lines diverge and how deep your preparation actually goes.") }}</p>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("How you actually learn it") }}</div>
          <h3>{{ t("Drill — Spaced Repetition") }}</h3>
          <p>{{ t("Daily drills using spaced repetition, so the moves you struggle with come back more often and the ones you know stay sharp with less effort.") }}</p>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("Where repertoires usually fail") }}</div>
          <h3>{{ t("Practice Into the Middle Game") }}</h3>
          <p>{{ t("Your repertoire ends — but the game doesn't. Practice extends play beyond your prepared lines into the middle game, surfacing the positions that leave you thinking 'What do I do now?'' That's where real preparation happens.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Export & Import") }}</h3>
          <p>{{ t("Export your repertoire for use in other tools. Import existing repertoires to build on work you've already done.") }}</p>
        </div>
      </section>

      <!-- ChessVis AI
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap purple">
            <ion-icon :icon="sparklesOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">ChessVis AI</h2>
          <span class="sub-badge">Subscriber</span>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">Included with subscription</div>
          <h3>Your Chess Coach, Built In</h3>
          <p>Subscribers get monthly AI credits to use directly inside ChessVis. No switching apps, no copy-pasting positions — the coach is right here where your games and repertoire already live.</p>
        </div>

        <div class="feature-card">
          <h3>Deep Game Analysis</h3>
          <p>Go beyond engine evaluations. ChessVis AI explains what happened in your game in plain language — identifying turning points, flagging critical moments, and telling you what you should have been thinking.</p>
        </div>

        <div class="feature-card">
          <h3>A Coach That Never Sleeps</h3>
          <p>Ask about a position, a line in your repertoire, or why a move was wrong. Get the kind of explanation a human coach would give — not just a number.</p>
        </div>

        <div class="feature-card">
          <h3>Use It or Lose It</h3>
          <p>Credits refresh every month. They don't roll over — which means there's always a reason to come back, analyze a game, and get better.</p>
        </div>
      </section> -->

      <!-- Legacy -->
      <section class="feature-section legacy-section">
        <div class="section-header">
          <div class="section-icon-wrap gray">
            <ion-icon :icon="timeOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Legacy Tools") }}</h2>
        </div>

        <div class="feature-card">
          <h3>{{ t("Classic Opening & Repertoire Tools") }}</h3>
          <p>{{ t("The original ChessVis opening and repertoire tools are still here — including the Win/Loss Explorer, Slice/Dice analysis, Play Against Game Collection, and Master Player Explorer. If you prefer the classic approach, nothing has been taken away.") }}</p>
        </div>
      </section>

      <!-- CTA -->
      <section class="cta-section">
        <ion-button expand="block" class="cta-button cta-primary" @click="onClick('studio_menu','Studio','program')">
          {{ t("Explore Studio") }}
        </ion-button>
        <ion-button expand="block" fill="outline" class="cta-button cta-secondary" @click="onClick('subscribe_blurb','Subscribe','you')">
          {{ t("View Subscription Options") }}
        </ion-button>
        <p class="cta-note">{{ t("Opening Mastery Explorer is free. Full access unlocks with a subscription.") }}</p>
      </section>

    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonContent, IonIcon, IonButton,
} from '@ionic/vue'
import {
  libraryOutline, trophyOutline, bookOutline,
  sparklesOutline, timeOutline, chevronBackOutline
} from 'ionicons/icons'
import BottomNav from '@/components/BottomNav.vue'
import { t } from '@/composables/translateText.js'

const emit = defineEmits(['menu-item-click'])

function onClick(path : string,label : string,whichMenu : string) {
  //console.log("Landing page CLICK ",path,label)
  emit("menu-item-click",path,label,whichMenu)
}
</script>

<style scoped>
ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content {
  --background: #1a1a2e;
}

/* ── Overview link ── */
.overview-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background: #0f0f22;
  border-bottom: 0.5px solid #2a2a4a;
  cursor: pointer;
  font-size: 13px;
  color: #AFA9EC;
}
 
.overview-link-icon {
  font-size: 15px;
}

/* ── Hero ── */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 24px 28px;
  border-bottom: 0.5px solid #2a2a4a;
}

.hero-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: #3C3489;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.hero-icon {
  font-size: 34px;
  color: #CECBF6;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 10px;
}

.hero-tagline {
  font-size: 16px;
  font-weight: 500;
  color: #AFA9EC;
  margin: 0 0 10px;
  line-height: 1.4;
}

.hero-sub {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0 0 16px;
}

.hero-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.badge {
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 99px;
}

.badge-free {
  background: #085041;
  color: #9FE1CB;
}

.badge-sub {
  background: #3C3489;
  color: #CECBF6;
}

/* ── Feature sections ── */
.feature-section {
  padding: 24px 16px 8px;
  border-bottom: 0.5px solid #2a2a4a;
}

.legacy-section {
  opacity: 0.85;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.section-icon-wrap.purple {
  background: #3C3489;
  color: #CECBF6;
}

.section-icon-wrap.gray {
  background: #2a2a3a;
  color: #888780;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  flex: 1;
}

.sub-badge {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #AFA9EC;
  background: #3C3489;
  padding: 3px 8px;
  border-radius: 99px;
}

.section-intro {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0 0 14px;
}

/* ── Feature cards ── */
.feature-card {
  background: #1e1e35;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  border: 0.5px solid #2a2a4a;
}

.feature-card.highlight {
  border-color: #534AB7;
  background: #1a1535;
}

.feature-card-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7F77DD;
  margin-bottom: 6px;
}

.feature-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 6px;
}

.feature-card p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.6;
  margin: 0;
}

/* ── CTA ── */
.cta-section {
  padding: 28px 20px 36px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cta-button {
  --border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  height: 52px;
}

.cta-primary {
  --background: #534AB7;
  --background-activated: #3C3489;
  --color: #ffffff;
}

.cta-secondary {
  --color: #AFA9EC;
  --border-color: #534AB7;
}

.cta-note {
  font-size: 12px;
  color: #555570;
  margin: 4px 0 0;
  text-align: center;
  line-height: 1.5;
}
</style>
